// Gestaltung der Navigations-Hotspots, pro Tour einstellbar (tours.settings).
// Bewusst kuratierte Optionen statt Freiform-CSS — jede Kombination bleibt
// auf hellen wie dunklen Panoramen lesbar.

export type HotspotVariant = "dot" | "ring" | "arrow" | "pill";
export type HotspotSize = "s" | "m" | "l";

export type HotspotStyle = {
  variant: HotspotVariant;
  /** Akzentfarbe als #rrggbb */
  color: string;
  size: HotspotSize;
  /** Dauerhaftes sanftes Pulsieren (Hover-Effekt bleibt davon unberührt) */
  pulse: boolean;
};

export const HOTSPOT_VARIANTS: HotspotVariant[] = ["dot", "ring", "arrow", "pill"];
export const HOTSPOT_SIZES: HotspotSize[] = ["s", "m", "l"];

export const DEFAULT_HOTSPOT_STYLE: HotspotStyle = {
  variant: "dot",
  color: "#ffffff",
  size: "m",
  pulse: true,
};

/** Skalierungsfaktor je Größe — CSS und Marker-Maße nutzen denselben Wert. */
export const HOTSPOT_SIZE_SCALE: Record<HotspotSize, number> = { s: 0.78, m: 1, l: 1.3 };

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** Unbekanntes aus jsonb/Snapshots → garantiert vollständiger Style (Feld-weise Fallbacks). */
export function normalizeHotspotStyle(value: unknown): HotspotStyle {
  const raw = (value ?? {}) as Partial<Record<keyof HotspotStyle, unknown>>;
  return {
    variant: HOTSPOT_VARIANTS.includes(raw.variant as HotspotVariant)
      ? (raw.variant as HotspotVariant)
      : DEFAULT_HOTSPOT_STYLE.variant,
    color:
      typeof raw.color === "string" && HEX_COLOR.test(raw.color)
        ? raw.color.toLowerCase()
        : DEFAULT_HOTSPOT_STYLE.color,
    size: HOTSPOT_SIZES.includes(raw.size as HotspotSize)
      ? (raw.size as HotspotSize)
      : DEFAULT_HOTSPOT_STYLE.size,
    pulse: typeof raw.pulse === "boolean" ? raw.pulse : DEFAULT_HOTSPOT_STYLE.pulse,
  };
}

// ── Marker-Markup ────────────────────────────────────────────────────
// Bewusst hier und nicht im Viewer: die Beispielansicht in den Tour-
// Einstellungen baut denselben Marker, damit Vorschau und Tour nicht
// auseinanderlaufen. Passendes CSS: shared/hotspot.css.

const escapeHtml = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

// Chevron-Up (Lucide) — Farbe kommt per currentColor aus dem CSS
const ARROW_CORE_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m18 15-6-6-6 6"/></svg>';

export type HotspotMarker = {
  html: string;
  /** Kantenlänge in px — null bei der Beschriftung, die mit dem Text wächst */
  sizePx: number | null;
};

/** Marker-HTML zu einem Stil; `label` beschriftet nur die Pill-Variante. */
export function hotspotMarkerHtml(style: HotspotStyle, label = ""): HotspotMarker {
  // Beschriftungs-Variante nur mit Text — sonst Rückfall auf den Punkt
  if (style.variant === "pill" && label) {
    return {
      html: `<div class="f360-pill"><span class="f360-pill-dot f360-hs-core"></span>${escapeHtml(label)}</div>`,
      sizePx: null,
    };
  }
  const core =
    style.variant === "ring"
      ? '<div class="f360-hs-core f360-ring-core"></div>'
      : style.variant === "arrow"
        ? `<div class="f360-hs-core f360-arrow-core">${ARROW_CORE_SVG}</div>`
        : '<div class="f360-hs-core f360-dot-core"></div>';
  return {
    html: `<div class="f360-dot">${core}</div>`,
    sizePx: Math.round(36 * HOTSPOT_SIZE_SCALE[style.size]),
  };
}
